import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { api, formatEUR, formatDateFR, formatApiError } from "../../lib/api";
import { toast } from "sonner";
import {
  ArrowLeft, ChevronRight, ChevronLeft, CheckCircle2, Circle, Camera, Gauge,
  AlertTriangle, PenLine, KeyRound, ClipboardCheck, User, Car, Calendar, Loader2,
  ShieldCheck, Scale, Ban, Hash, FileText,
} from "lucide-react";
import {
  StepCard, InfoRow, RecapTile, SignedBanner, PresentialSignBlock, StepPhotos,
  StepReadings, StepDamages, CreateEdlPrompt, DocsEmailStatus, cautionLabel, saveDamages,
} from "./DepartureFlow";

const STEPS = [
  { key: "verify", label: "Vérification", icon: ClipboardCheck },
  { key: "photos", label: "Photos", icon: Camera },
  { key: "readings", label: "Relevés", icon: Gauge },
  { key: "damages", label: "Dommages", icon: AlertTriangle },
  { key: "bilan", label: "Bilan", icon: Scale },
  { key: "caution", label: "Caution", icon: ShieldCheck },
  { key: "sign", label: "Signature", icon: PenLine },
  { key: "close", label: "Clôture", icon: KeyRound },
];

const SEV = { minor: "Léger", medium: "Moyen", major: "Important" };
const DIFF_FR = { new: "Nouveau dommage", worse: "Aggravation", resolved: "Résolu", same: "Déjà présent au départ", ok: "RAS" };

export default function ReturnFlow() {
  const { bookingId } = useParams();
  const navigate = useNavigate();
  const [state, setState] = useState(null);
  const [loading, setLoading] = useState(true);
  const [step, setStep] = useState(0);

  const load = () =>
    api.get(`/vendor/bookings/${bookingId}/return-state`)
      .then(({ data }) => { setState(data); return data; })
      .catch(() => setState(null))
      .finally(() => setLoading(false));
  useEffect(() => { load(); }, [bookingId]); // eslint-disable-line

  const insp = state?.inspection;
  const setInsp = (u) => setState((s) => ({ ...s, inspection: u }));
  const edlLocked = !!(insp?.client_signature || insp?.vendor_signature || insp?.client_refusal);

  const ensureInspection = async () => {
    if (state?.inspection) return state.inspection;
    try {
      const { data } = await api.post(`/inspections?booking_id=${bookingId}&type=return`);
      setState((s) => ({ ...s, inspection: data.inspection }));
      return data.inspection;
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Impossible de créer l'état des lieux de retour");
      return null;
    }
  };

  const [kmException, setKmException] = useState("");
  const saveReturnReadings = async () => {
    if (!insp || edlLocked) return true;
    try {
      await api.put(`/inspections/${insp.id}`, {
        step: "mileage", mileage: insp.mileage, fuel_level: insp.fuel_level,
        battery_level: insp.battery_level ?? null,
        mileage_exception_reason: kmException.trim() || null,
      });
      return true;
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Erreur d'enregistrement");
      return false;
    }
  };

  const goTo = async (i) => {
    const currentKey = STEPS[step]?.key;
    if (insp && !edlLocked && i !== step) {
      if (currentKey === "readings") { const ok = await saveReturnReadings(); if (!ok) return; }
      if (currentKey === "damages") await saveDamages(insp);
    }
    if (["photos", "readings", "damages"].includes(STEPS[i]?.key) && !state?.inspection) await ensureInspection();
    setStep(i);
    window.scrollTo({ top: 0 });
    if (["bilan", "sign", "close"].includes(STEPS[i]?.key)) load();
  };

  const done = useMemo(() => {
    if (!state) return {};
    const c = state.checklist || {};
    return {
      verify: c.control_started,
      photos: c.photos_done,
      readings: c.mileage_done && (!!insp?.fuel_level || insp?.battery_level != null),
      damages: c.signature_done,
      bilan: c.signature_done,
      caution: c.caution_decided && c.caution_required !== false ? c.caution_decided : c.caution_decided,
      sign: c.signature_done,
      close: c.keys_returned,
    };
  }, [state, insp]);

  const doneCount = Object.values(done).filter(Boolean).length;
  const progressPct = Math.round((doneCount / STEPS.length) * 100);

  if (loading) return <div className="min-h-screen bg-slate-50 flex items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-sky-600" /></div>;
  if (!state) {
    return (
      <div className="min-h-screen bg-slate-50 flex flex-col items-center justify-center px-6 text-center" data-testid="return-not-found">
        <h2 className="font-heading text-lg font-semibold text-slate-900">Réservation introuvable</h2>
        <button onClick={() => navigate("/vendor/pilotage?tab=today")} className="mt-4 rounded-full bg-slate-900 text-white text-sm font-semibold px-5 py-2.5">Retour aux opérations du jour</button>
      </div>
    );
  }

  const { booking, checklist } = state;
  const stepKey = STEPS[step].key;

  return (
    <div className="min-h-screen bg-slate-50 pb-28" data-testid="return-flow">
      <div className="sticky top-0 z-30 bg-white/95 backdrop-blur border-b border-slate-200">
        <div className="max-w-2xl mx-auto px-4 pt-3">
          <div className="flex items-center gap-3">
            <button onClick={() => navigate("/vendor/pilotage?tab=today")} className="h-9 w-9 rounded-full border border-slate-200 flex items-center justify-center text-slate-600 hover:bg-slate-50 shrink-0" data-testid="return-back-btn">
              <ArrowLeft className="h-4 w-4" />
            </button>
            <div className="min-w-0 flex-1">
              <div className="text-[10px] uppercase tracking-[0.15em] font-bold text-sky-600">Retour · {booking.reference || booking.id.slice(0, 8).toUpperCase()}</div>
              <div className="text-sm font-semibold text-slate-900 truncate">{booking.client_name} — {booking.vehicle_name}</div>
            </div>
            <div className="text-xs font-bold text-slate-500 shrink-0" data-testid="return-progress-pct">{progressPct}%</div>
          </div>
          <div className="h-1.5 rounded-full bg-slate-100 mt-2.5 overflow-hidden">
            <div className="h-full bg-sky-600 rounded-full transition-all duration-500" style={{ width: `${progressPct}%` }} />
          </div>
          <div className="flex gap-1.5 overflow-x-auto py-2.5 -mx-1 px-1 scrollbar-none">
            {STEPS.map((s, i) => {
              const isDone = done[s.key];
              const active = i === step;
              return (
                <button key={s.key} onClick={() => goTo(i)}
                  className={`inline-flex items-center gap-1.5 px-3 h-8 rounded-full text-[11px] font-semibold whitespace-nowrap shrink-0 border transition-colors ${
                    active ? "bg-slate-900 text-white border-slate-900"
                      : isDone ? "bg-emerald-50 text-emerald-700 border-emerald-200"
                      : "bg-white text-slate-500 border-slate-200 hover:border-slate-300"
                  }`}
                  data-testid={`return-step-${s.key}`}>
                  {isDone ? <CheckCircle2 className="h-3.5 w-3.5" /> : <s.icon className="h-3.5 w-3.5" />}
                  {i + 1}. {s.label}
                </button>
              );
            })}
          </div>
        </div>
      </div>

      <div className="max-w-2xl mx-auto px-4 py-5">
        {stepKey === "verify" && <StepVerifyReturn state={state} onStarted={load} />}
        {stepKey === "photos" && (insp
          ? <StepPhotos insp={insp} setInsp={setInsp} locked={edlLocked} onChanged={load} />
          : <CreateEdlPrompt onCreate={ensureInspection} />)}
        {stepKey === "readings" && (insp ? (
          <StepReadings
            insp={insp} setInsp={setInsp} vehicle={state.vehicle} locked={edlLocked}
            title="Kilométrage & énergie au retour"
            instruction="Relevez le compteur au moment de la restitution."
            minMileage={state.departure?.mileage}
          >
            <ReturnCalcs state={state} insp={insp} kmException={kmException} setKmException={setKmException} />
          </StepReadings>
        ) : <CreateEdlPrompt onCreate={ensureInspection} />)}
        {stepKey === "damages" && (insp ? (
          <StepDamages
            insp={insp} setInsp={setInsp} locked={edlLocked}
            departureZones={state.departure?.damaged_zones || []}
            title="État des lieux de retour"
            instruction="Comparez chaque zone avec le départ. Ne signalez comme « nouveau » que ce qui n'existait pas."
          />
        ) : <CreateEdlPrompt onCreate={ensureInspection} />)}
        {stepKey === "bilan" && <StepBilan state={state} onValidated={load} />}
        {stepKey === "caution" && <StepCaution state={state} bookingId={bookingId} onDecided={load} />}
        {stepKey === "sign" && <StepSignReturn state={state} insp={insp} onCreate={ensureInspection} onSigned={load} />}
        {stepKey === "close" && <StepClose bookingId={bookingId} state={state} onDone={load} goTo={(k) => goTo(STEPS.findIndex((s) => s.key === k))} />}
      </div>

      <div className="fixed bottom-0 inset-x-0 z-30 bg-white border-t border-slate-200 px-4 py-3">
        <div className="max-w-2xl mx-auto flex items-center justify-between gap-3">
          <button onClick={() => goTo(Math.max(0, step - 1))} disabled={step === 0}
            className="inline-flex items-center gap-1.5 h-11 px-4 rounded-xl border border-slate-200 text-sm font-medium text-slate-600 disabled:opacity-30"
            data-testid="return-prev">
            <ChevronLeft className="h-4 w-4" /> Précédent
          </button>
          {step < STEPS.length - 1 ? (
            <button onClick={() => goTo(step + 1)}
              className="inline-flex items-center gap-1.5 h-11 px-6 rounded-xl bg-sky-600 text-white text-sm font-bold hover:bg-sky-700"
              data-testid="return-next">
              Continuer <ChevronRight className="h-4 w-4" />
            </button>
          ) : (
            <div className="text-xs font-semibold text-slate-500">{checklist.keys_returned ? "Location clôturée ✓" : "Dernière étape"}</div>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── Étape 1 : Vérification de la location ───
function StepVerifyReturn({ state, onStarted }) {
  const { booking, vehicle, departure, checklist } = state;
  const [starting, setStarting] = useState(false);
  const paid = booking.amount_paid || 0;
  const balance = Math.round(((booking.total || 0) - paid) * 100) / 100;

  const start = async () => {
    setStarting(true);
    try {
      await api.post(`/vendor/bookings/${booking.id}/return/start`);
      toast.success("Contrôle du retour démarré");
      onStarted();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Erreur");
    } finally { setStarting(false); }
  };

  return (
    <StepCard title="Vérification de la location" instruction="Vérifiez la location et l'état de départ avant de commencer le contrôle." testid="step-return-verify">
      {booking.status !== "in_progress" && !checklist.keys_returned && (
        <div className="mb-3 flex items-start gap-2 rounded-xl bg-amber-50 border border-amber-200 px-4 py-3 text-xs font-semibold text-amber-700" data-testid="return-status-warning">
          <AlertTriangle className="h-4 w-4 shrink-0" />
          Cette réservation n'est pas au statut « En cours » ({booking.status}). Le retour ne peut être clôturé que pour une location en cours — terminez d'abord le départ.
        </div>
      )}
      <div className="space-y-3">
        <div className="rounded-2xl border border-slate-200 bg-white p-4" data-testid="return-verify-booking">
          <div className="text-[10px] uppercase tracking-[0.15em] font-bold text-slate-400 mb-2">Location</div>
          <InfoRow icon={User} label="Client" value={`${booking.client_name} · ${booking.client_phone || "—"}`} />
          <InfoRow icon={Car} label="Véhicule" value={`${booking.vehicle_name} · ${vehicle.license_plate || "—"}`} />
          <InfoRow icon={Calendar} label="Départ" value={`${formatDateFR(booking.start_date)} ${booking.start_time || ""}${booking.keys_handed_at ? ` — clés remises le ${new Date(booking.keys_handed_at).toLocaleString("fr-FR", { dateStyle: "short", timeStyle: "short" })}` : ""}`} />
          <InfoRow icon={Calendar} label="Retour prévu" value={`${formatDateFR(booking.end_date)} ${booking.end_time || ""}`} />
          <InfoRow icon={FileText} label="Paiement" value={`${formatEUR(paid)} payé · ${balance > 0 ? `${formatEUR(balance)} restant` : "soldé"}`} warn={balance > 0} />
          <InfoRow icon={ShieldCheck} label="Caution" value={booking.caution_amount ? `${formatEUR(booking.caution_amount)} (${cautionLabel(booking.caution_status)})` : "Non demandée"} />
        </div>
        {departure && (
          <div className="rounded-2xl border border-violet-200 bg-violet-50/40 p-4" data-testid="return-verify-departure">
            <div className="text-[10px] uppercase tracking-[0.15em] font-bold text-violet-500 mb-2">État au départ (référence)</div>
            <InfoRow icon={Gauge} label="Kilométrage départ" value={departure.mileage != null ? `${departure.mileage.toLocaleString("fr-FR")} km` : "—"} />
            <InfoRow icon={Gauge} label="Énergie départ" value={departure.battery_level != null ? `${departure.battery_level} %` : (departure.fuel_level || "—")} />
            <InfoRow icon={AlertTriangle} label="Dommages au départ" value={departure.damaged_zones.length ? departure.damaged_zones.map((z) => `${z.name} (${SEV[z.severity] || "signalé"})`).join(", ") : "Aucun"} />
          </div>
        )}
        {!departure && (
          <div className="rounded-xl bg-amber-50 border border-amber-200 px-4 py-3 text-xs font-semibold text-amber-700" data-testid="return-no-departure-edl">
            Aucun état des lieux de départ trouvé — la comparaison sera impossible.
          </div>
        )}
      </div>
      {checklist.control_started ? (
        <div className="mt-4 flex items-center gap-2 rounded-xl bg-emerald-50 border border-emerald-200 px-4 py-3 text-sm font-semibold text-emerald-700" data-testid="return-started-banner">
          <CheckCircle2 className="h-4 w-4" /> Contrôle du retour démarré
        </div>
      ) : (
        <button onClick={start} disabled={starting || booking.status !== "in_progress"}
          className="mt-4 w-full inline-flex items-center justify-center gap-2 h-12 rounded-xl bg-slate-900 text-white text-sm font-bold hover:bg-slate-800 disabled:opacity-40"
          data-testid="return-start-btn">
          {starting ? <Loader2 className="h-4 w-4 animate-spin" /> : <ClipboardCheck className="h-4 w-4" />} Commencer le contrôle du retour
        </button>
      )}
    </StepCard>
  );
}

// ─── Calculs auto affichés dans l'étape Relevés ───
function ReturnCalcs({ state, insp, kmException, setKmException }) {
  const depKm = state.departure?.mileage;
  const kmRun = insp.mileage != null && depKm != null ? insp.mileage - depKm : null;
  const included = state.kms?.included;
  const overage = kmRun != null && included ? Math.max(0, kmRun - included) : null;
  const fee = overage && state.vehicle?.price_extra_km ? Math.round(overage * state.vehicle.price_extra_km * 100) / 100 : null;
  const incoherent = kmRun != null && kmRun < 0;
  const fuelDep = state.departure?.fuel_level;
  const battDep = state.departure?.battery_level;

  return (
    <div className="pt-2 border-t border-slate-100 space-y-2" data-testid="return-calcs">
      <div className="grid grid-cols-2 gap-2">
        <RecapTile label="Km parcourus" value={kmRun != null ? `${kmRun.toLocaleString("fr-FR")} km` : "—"} warn={incoherent} />
        <RecapTile label="Km inclus" value={included ? `${included.toLocaleString("fr-FR")} km` : "Illimité / non défini"} />
        {overage != null && overage > 0 && <RecapTile label="Dépassement" value={`${overage.toLocaleString("fr-FR")} km`} warn />}
        {fee != null && <RecapTile label="Frais km suggérés" value={formatEUR(fee)} warn />}
        {(fuelDep || battDep != null) && (
          <RecapTile label="Énergie départ → retour" value={battDep != null
            ? `${battDep} % → ${insp.battery_level ?? "—"} %`
            : `${fuelDep || "—"} → ${insp.fuel_level || "—"}`} />
        )}
      </div>
      {incoherent && (
        <div className="rounded-lg bg-amber-50 border border-amber-200 px-3 py-2" data-testid="return-km-incoherent">
          <div className="text-xs font-bold text-amber-800 mb-1">Kilométrage retour inférieur au départ — justification obligatoire</div>
          <textarea value={kmException} onChange={(e) => setKmException(e.target.value)} rows={2}
            className="w-full rounded-lg border border-amber-300 px-3 py-2 text-xs outline-none"
            placeholder="Ex : compteur remplacé, erreur de saisie au départ corrigée…"
            data-testid="return-km-exception-input" />
        </div>
      )}
    </div>
  );
}

// ─── Étape 5 : Bilan de restitution ───
function StepBilan({ state, onValidated }) {
  const { comparison, kms, booking } = state;
  const [validatingFees, setValidatingFees] = useState(false);
  const problems = (comparison || []).filter((d) => ["new", "worse"].includes(d.status));
  const fees = booking.return_fees || {};
  const feesValidated = fees.km_fee != null || fees.km_overage != null;

  const validateKmFees = async () => {
    setValidatingFees(true);
    try {
      await api.post(`/vendor/bookings/${booking.id}/return-fees`, { km_overage: kms.overage, km_fee: kms.suggested_fee });
      toast.success("Frais kilométriques validés");
      onValidated();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Erreur");
    } finally { setValidatingFees(false); }
  };

  return (
    <StepCard title="Bilan de restitution" instruction="Récapitulatif comparatif départ → retour. Vérifiez chaque point avant la caution." testid="step-return-bilan">
      <div className="grid grid-cols-2 gap-2.5 mb-4">
        <RecapTile label="Km départ → retour" value={`${kms.departure ?? "—"} → ${kms.return ?? "—"}`} />
        <RecapTile label="Km parcourus" value={kms.run != null ? `${kms.run.toLocaleString("fr-FR")} km` : "—"} />
        <RecapTile label="Nouveaux dommages" value={String((comparison || []).filter((d) => d.status === "new").length)} warn={(comparison || []).some((d) => d.status === "new")} />
        <RecapTile label="Aggravations" value={String((comparison || []).filter((d) => d.status === "worse").length)} warn={(comparison || []).some((d) => d.status === "worse")} />
      </div>

      {(comparison || []).length === 0 ? (
        <div className="rounded-xl bg-slate-50 px-4 py-3 text-xs font-semibold text-slate-500 mb-4" data-testid="bilan-no-comparison">
          Comparaison indisponible : réalisez d'abord les relevés et dommages du retour.
        </div>
      ) : problems.length === 0 ? (
        <div className="rounded-xl bg-emerald-50 border border-emerald-200 px-4 py-3 text-sm font-semibold text-emerald-700 mb-4" data-testid="bilan-no-problem">
          Aucun nouveau dommage ni aggravation par rapport au départ. 👏
        </div>
      ) : (
        <div className="space-y-1.5 mb-4" data-testid="bilan-diff-list">
          {problems.map((d) => (
            <div key={d.zone} className="flex items-center justify-between rounded-xl bg-white border border-slate-200 px-3 py-2.5 text-xs">
              <span className="font-semibold text-slate-800">{d.zone}{d.to_comment ? ` — ${d.to_comment}` : ""}</span>
              <span className={`px-2 py-0.5 rounded-full font-bold text-[10px] ${d.status === "new" ? "bg-red-100 text-red-700" : "bg-amber-100 text-amber-700"}`}>
                {d.status === "new" ? `Nouveau · ${SEV[d.to_severity] || "—"}` : `${SEV[d.from_severity] || "—"} → ${SEV[d.to_severity] || "—"}`}
              </span>
            </div>
          ))}
        </div>
      )}

      {kms.overage != null && kms.overage > 0 && (
        <div className="rounded-xl border border-amber-200 bg-amber-50 p-4 mb-4" data-testid="bilan-km-fees">
          <div className="text-sm font-bold text-amber-800 mb-1">Dépassement kilométrique : {kms.overage.toLocaleString("fr-FR")} km</div>
          <p className="text-xs text-amber-700 mb-2">
            {kms.suggested_fee != null
              ? `Frais suggérés selon votre tarif (${formatEUR(state.vehicle.price_extra_km)}/km) : ${formatEUR(kms.suggested_fee)}. Rien n'est facturé sans votre validation explicite.`
              : "Aucun tarif de km supplémentaire n'est configuré sur ce véhicule — aucun montant ne sera calculé automatiquement."}
          </p>
          {feesValidated ? (
            <div className="text-xs font-bold text-emerald-700" data-testid="bilan-fees-validated">✓ Frais validés : {fees.km_fee != null ? formatEUR(fees.km_fee) : `${fees.km_overage} km`}</div>
          ) : kms.suggested_fee != null && (
            <button onClick={validateKmFees} disabled={validatingFees}
              className="h-9 px-4 rounded-lg bg-amber-600 text-white text-xs font-bold disabled:opacity-50"
              data-testid="bilan-validate-fees-btn">
              Valider ces frais ({formatEUR(kms.suggested_fee)})
            </button>
          )}
        </div>
      )}
      <div className="text-[11px] text-slate-500">Le bilan complet (avec photos comparatives) figurera dans le PDF « Bilan de restitution » envoyé au client après la clôture.</div>
    </StepCard>
  );
}

// ─── Étape 6 : Caution ───
const CAUTION_ACTIONS = [
  { key: "release_full", label: "Restitution totale", desc: "Aucune retenue — la caution sera rendue au client." },
  { key: "retain_partial", label: "Retenue partielle", desc: "Retenir un montant, restituer le reste." },
  { key: "retain_full", label: "Retenue totale", desc: "Retenir l'intégralité de la caution." },
  { key: "defer", label: "Décision différée", desc: "Trancher plus tard (devis de réparation, expertise…)." },
];

function StepCaution({ state, bookingId, onDecided }) {
  const { booking, caution } = state;
  const [action, setAction] = useState(null);
  const [amount, setAmount] = useState("");
  const [reason, setReason] = useState("");
  const [saving, setSaving] = useState(false);
  const decision = caution.decision;

  if (!caution.amount) {
    return (
      <StepCard title="Caution & règlement final" instruction="Aucune caution n'était demandée pour cette location." testid="step-return-caution">
        <div className="rounded-xl bg-slate-50 px-4 py-4 text-sm font-semibold text-slate-500" data-testid="caution-none">
          Pas de caution à traiter — vous pouvez passer à la signature.
        </div>
      </StepCard>
    );
  }

  const retainNeeded = ["retain_partial", "retain_full"].includes(action);
  const canSave = action && (!retainNeeded || ((action === "retain_full" || parseFloat(amount) > 0) && reason.trim()));

  const save = async () => {
    setSaving(true);
    try {
      await api.post(`/vendor/bookings/${bookingId}/caution-decision`, {
        action,
        retain_amount: action === "retain_partial" ? parseFloat(amount) : undefined,
        reason: reason.trim(),
      });
      toast.success("Décision de caution enregistrée");
      onDecided();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Erreur");
    } finally { setSaving(false); }
  };

  const ACTION_FR = { release_full: "Restitution totale", release_partial: "Restitution partielle", retain_partial: "Retenue partielle", retain_full: "Retenue totale", defer: "Décision différée" };

  return (
    <StepCard title="Caution & règlement final" instruction={`Caution de ${formatEUR(caution.amount)} (${cautionLabel(caution.status)}). Enregistrez votre décision — traçée, sans fausse confirmation bancaire.`} testid="step-return-caution">
      {decision ? (
        <div className="rounded-2xl border border-emerald-200 bg-emerald-50 p-4" data-testid="caution-decision-done">
          <div className="text-sm font-bold text-emerald-700 mb-1 flex items-center gap-2"><CheckCircle2 className="h-4 w-4" /> {ACTION_FR[decision.action] || decision.action}</div>
          {decision.retain_amount ? <div className="text-xs text-slate-700">Retenue : <b>{formatEUR(decision.retain_amount)}</b> — {decision.reason}</div> : null}
          {decision.refund_amount != null && <div className="text-xs text-slate-700">À restituer : <b>{formatEUR(decision.refund_amount)}</b></div>}
          <div className="text-[11px] text-slate-500 mt-2">
            {decision.execution === "deferred"
              ? "Décision différée — le client sera informé du traitement en cours."
              : "⚠ Aucun remboursement bancaire n'a été effectué automatiquement : le traitement (Stripe ou virement) reste à exécuter manuellement."}
          </div>
        </div>
      ) : (
        <>
          <div className="space-y-2 mb-4">
            {CAUTION_ACTIONS.map((a) => (
              <button key={a.key} onClick={() => setAction(a.key)}
                className={`w-full text-left rounded-2xl border-2 p-3.5 transition-colors ${action === a.key ? "border-sky-600 bg-sky-50" : "border-slate-200 bg-white hover:border-slate-300"}`}
                data-testid={`caution-action-${a.key}`}>
                <div className="text-sm font-bold text-slate-900">{a.label}</div>
                <div className="text-xs text-slate-500">{a.desc}</div>
              </button>
            ))}
          </div>
          {retainNeeded && (
            <div className="rounded-xl border border-slate-200 bg-white p-4 mb-4 space-y-3" data-testid="caution-retain-form">
              {action === "retain_partial" && (
                <div>
                  <label className="text-xs font-semibold text-slate-700 block mb-1">Montant à retenir (max {formatEUR(caution.amount)}) <span className="text-red-500">*</span></label>
                  <input type="number" inputMode="decimal" min="0.01" max={caution.amount} step="0.01" value={amount}
                    onChange={(e) => setAmount(e.target.value)}
                    className="w-full rounded-xl border border-slate-200 px-4 py-3 text-sm font-semibold outline-none focus:ring-2 focus:ring-sky-600"
                    placeholder="Ex : 250" data-testid="caution-retain-amount" />
                </div>
              )}
              <div>
                <label className="text-xs font-semibold text-slate-700 block mb-1">Motif de la retenue <span className="text-red-500">*</span></label>
                <textarea value={reason} onChange={(e) => setReason(e.target.value)} rows={2}
                  className="w-full rounded-xl border border-slate-200 px-4 py-3 text-sm outline-none focus:ring-2 focus:ring-sky-600"
                  placeholder="Ex : rayure profonde portière droite constatée au retour (voir EDL)…"
                  data-testid="caution-retain-reason" />
              </div>
            </div>
          )}
          <button onClick={save} disabled={!canSave || saving}
            className="w-full h-12 rounded-xl bg-slate-900 text-white text-sm font-bold disabled:opacity-40"
            data-testid="caution-save-btn">
            {saving ? "Enregistrement…" : "Enregistrer la décision"}
          </button>
        </>
      )}
    </StepCard>
  );
}

// ─── Étape 7 : Signature du client au retour ───
function StepSignReturn({ state, insp, onCreate, onSigned }) {
  const [signing, setSigning] = useState(false);
  const [refusing, setRefusing] = useState(false);
  const [showRefuse, setShowRefuse] = useState(false);
  const [refuseReason, setRefuseReason] = useState("");
  const [exceptionReason, setExceptionReason] = useState("");
  const [showException, setShowException] = useState(false);

  if (!insp) return <CreateEdlPrompt onCreate={onCreate} />;

  const signed = !!insp.client_signature;
  const refused = !!insp.client_refusal;
  const zonesMissing = (insp.zones || []).filter((z) => !(z.photos || []).length).map((z) => z.name);
  const problems = (state.comparison || []).filter((d) => ["new", "worse"].includes(d.status));
  const decision = state.caution?.decision;

  const sign = async ({ signature_data, signer_name, consent, reserve_text }) => {
    setSigning(true);
    try {
      const { data } = await api.put(`/inspections/${insp.id}`, {
        step: "sign_client_presential", signature_data, signer_name, consent,
        reserve_text: reserve_text || null,
        photos_exception_reason: exceptionReason.trim() || null,
      });
      toast.success(reserve_text ? "État des lieux signé avec réserve" : "État des lieux de retour signé");
      onSigned();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Erreur lors de la signature");
    } finally { setSigning(false); }
  };

  const refuse = async () => {
    setRefusing(true);
    try {
      await api.put(`/inspections/${insp.id}`, { step: "refuse_sign", refusal_reason: refuseReason.trim() });
      toast.success("Refus de signature enregistré et documenté");
      onSigned();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Erreur");
    } finally { setRefusing(false); }
  };

  return (
    <StepCard title="Signature du client" instruction="Présentez le bilan au client, recueillez sa signature (avec ou sans réserve) ou documentez un refus." testid="step-return-sign">
      {/* Récap présenté au client */}
      <div className="rounded-2xl border border-slate-200 bg-white p-4" data-testid="return-sign-recap">
        <div className="text-[10px] uppercase tracking-[0.15em] font-bold text-slate-400 mb-3">Bilan présenté au client — {insp.reference || "EDL retour"}</div>
        <div className="grid grid-cols-2 gap-2.5 text-sm mb-3">
          <RecapTile label="Km parcourus" value={state.kms?.run != null ? `${state.kms.run.toLocaleString("fr-FR")} km` : "—"} />
          <RecapTile label={insp.battery_level != null ? "Batterie" : "Carburant"} value={insp.battery_level != null ? `${insp.battery_level} %` : (insp.fuel_level || "—")} />
        </div>
        {problems.length ? (
          <div className="space-y-1 mb-2">
            {problems.map((d) => (
              <div key={d.zone} className="flex items-center justify-between text-xs bg-amber-50 border border-amber-100 rounded-lg px-3 py-2">
                <span className="font-semibold text-slate-800">{d.zone}</span>
                <span className="font-bold text-amber-700">{DIFF_FR[d.status]} · {SEV[d.to_severity] || "—"}</span>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-xs text-emerald-700 bg-emerald-50 border border-emerald-100 rounded-lg px-3 py-2 mb-2">Aucun nouveau dommage constaté</div>
        )}
        {decision && (
          <div className="text-xs text-slate-700 bg-slate-50 rounded-lg px-3 py-2" data-testid="return-sign-caution-line">
            Caution : {decision.retain_amount ? `retenue de ${formatEUR(decision.retain_amount)} — ${decision.reason}` : decision.action === "defer" ? "décision en cours de traitement" : "restitution totale enregistrée"}
          </div>
        )}
      </div>

      {signed ? (
        <SignedBanner sig={insp.client_signature} docLabel={insp.client_signature.reserve ? "l'état des lieux de retour (AVEC RÉSERVE)" : "l'état des lieux de retour"} />
      ) : refused ? (
        <div className="mt-4 rounded-2xl border border-red-200 bg-red-50 p-4" data-testid="refusal-banner">
          <div className="flex items-center gap-2 text-sm font-bold text-red-700 mb-1"><Ban className="h-4 w-4" /> Refus de signature enregistré</div>
          <div className="text-xs text-red-800">
            Le {new Date(insp.client_refusal.refused_at).toLocaleString("fr-FR", { dateStyle: "short", timeStyle: "short" })} par {insp.client_refusal.recorded_by}
            {insp.client_refusal.reason ? ` — Motif : ${insp.client_refusal.reason}` : ""}
          </div>
          <p className="text-[11px] text-red-700 mt-1.5">L'état des lieux réalisé reste conservé et figé. Vous pouvez clôturer la location.</p>
        </div>
      ) : (
        <>
          {zonesMissing.length > 0 && (
            <div className="mt-4 rounded-xl bg-amber-50 border border-amber-200 p-4" data-testid="return-photos-missing">
              <div className="flex items-center gap-2 text-sm font-bold text-amber-800 mb-1">
                <AlertTriangle className="h-4 w-4" /> Photos manquantes : {zonesMissing.join(", ")}
              </div>
              {!showException ? (
                <button onClick={() => setShowException(true)} className="text-xs font-bold text-amber-800 underline" data-testid="return-exception-toggle">Justifier une exception</button>
              ) : (
                <textarea value={exceptionReason} onChange={(e) => setExceptionReason(e.target.value)} rows={2}
                  className="w-full rounded-lg border border-amber-300 px-3 py-2 text-xs outline-none"
                  placeholder="Motif de l'exception…" data-testid="return-exception-reason" />
              )}
            </div>
          )}
          <PresentialSignBlock
            defaultName={state.booking.client_name}
            mentionDoc="Je reconnais avoir pris connaissance de l'état des lieux de retour et du bilan de restitution ci-dessus (différences constatées, dommages, montants et retenues éventuelles) et je consens à signer électroniquement en présence du loueur, sur son appareil. Ma signature peut être assortie d'une réserve."
            onSign={sign}
            signing={signing}
            allowReserve
            blockedReason={zonesMissing.length > 0 && !exceptionReason.trim() ? "Ajoutez au moins 1 photo par zone (étape 2) ou justifiez une exception ci-dessus pour activer la signature." : null}
          />
          <div className="mt-4 rounded-xl border border-slate-200 bg-white p-4" data-testid="refuse-block">
            {!showRefuse ? (
              <button onClick={() => setShowRefuse(true)} className="inline-flex items-center gap-1.5 text-xs font-bold text-red-600 hover:underline" data-testid="refuse-toggle-btn">
                <Ban className="h-3.5 w-3.5" /> Le client refuse de signer
              </button>
            ) : (
              <>
                <div className="text-xs font-bold text-slate-800 mb-1.5">Documenter le refus de signature</div>
                <textarea value={refuseReason} onChange={(e) => setRefuseReason(e.target.value)} rows={2}
                  className="w-full rounded-lg border border-slate-200 px-3 py-2 text-xs outline-none mb-2"
                  placeholder="Motif ou commentaire (ex : le client conteste la retenue)…" data-testid="refuse-reason-input" />
                <button onClick={refuse} disabled={refusing}
                  className="h-9 px-4 rounded-lg bg-red-600 text-white text-xs font-bold disabled:opacity-50"
                  data-testid="refuse-confirm-btn">
                  {refusing ? "Enregistrement…" : "Enregistrer le refus (horodaté, tracé)"}
                </button>
              </>
            )}
          </div>
        </>
      )}
    </StepCard>
  );
}

// ─── Étape 8 : Clôture ───
function StepClose({ bookingId, state, onDone, goTo }) {
  const [confirming, setConfirming] = useState(false);
  const [docsEmail, setDocsEmail] = useState(null);
  const { checklist, booking } = state;
  const canConfirm = checklist.mileage_done && checklist.signature_done && checklist.caution_decided;

  useEffect(() => {
    if (checklist.keys_returned && !docsEmail) {
      api.get(`/vendor/bookings/${bookingId}/documents`)
        .then(({ data }) => setDocsEmail(data.return_docs_email))
        .catch(() => {});
    }
  }, [checklist.keys_returned]); // eslint-disable-line

  const confirm = async () => {
    setConfirming(true);
    try {
      const { data } = await api.post(`/vendor/bookings/${bookingId}/close-return`);
      setDocsEmail(data.docs_email || null);
      toast.success("Retour terminé — location clôturée");
      onDone();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Clôture impossible");
    } finally { setConfirming(false); }
  };

  if (checklist.keys_returned) {
    return (
      <div className="text-center py-8" data-testid="return-success">
        <div className="mx-auto h-16 w-16 rounded-full bg-emerald-100 flex items-center justify-center mb-4">
          <CheckCircle2 className="h-8 w-8 text-emerald-600" />
        </div>
        <h2 className="font-heading text-2xl font-semibold text-slate-900">Retour terminé — Location clôturée ✅</h2>
        <p className="text-sm text-slate-500 mt-2 mb-1">Le véhicule <span className="font-semibold text-slate-900">{booking.vehicle_name}</span> est restitué.</p>
        <p className="text-xs text-slate-400 mb-5">
          Réservation passée au statut <span className="font-bold text-slate-600">Terminée</span>
          {booking.keys_returned_at ? ` · ${new Date(booking.keys_returned_at).toLocaleString("fr-FR", { dateStyle: "short", timeStyle: "short" })}` : ""}
        </p>
        <DocsEmailStatus info={docsEmail} phase="return" bookingId={bookingId} onResent={setDocsEmail} />
        <div className="flex flex-col gap-2 max-w-xs mx-auto">
          <a href={`/vendor/bookings/${bookingId}`} className="h-11 rounded-xl bg-slate-900 text-white text-sm font-bold flex items-center justify-center" data-testid="return-open-booking">Ouvrir la réservation</a>
          <a href="/vendor/pilotage?tab=today" className="h-11 rounded-xl border border-slate-200 text-sm font-semibold text-slate-700 flex items-center justify-center" data-testid="return-back-today">Retour aux opérations du jour</a>
        </div>
      </div>
    );
  }

  const items = [
    { ok: checklist.control_started, label: "Contrôle du retour démarré", stepKey: "verify", required: false },
    { ok: checklist.photos_done, label: "Photos du retour", stepKey: "photos", required: false },
    { ok: checklist.mileage_done, label: "Kilométrage relevé", stepKey: "readings", required: true },
    { ok: checklist.signature_done, label: checklist.signature_state === "refused" ? "Refus de signature documenté" : "État des lieux signé par le client", stepKey: "sign", required: true },
    ...(checklist.caution_required ? [{ ok: checklist.caution_decided, label: "Décision de caution enregistrée", stepKey: "caution", required: true }] : []),
  ];

  return (
    <StepCard title="Restitution des clés & clôture" instruction="Dernière étape : vérifiez les prérequis puis clôturez la location." testid="step-return-close">
      <div className="rounded-2xl border border-slate-200 bg-white p-4 space-y-1" data-testid="close-checklist">
        {items.map((it) => (
          <div key={it.label} className="flex items-center justify-between py-2 border-b border-slate-50 last:border-0">
            <div className="flex items-center gap-2.5 text-sm">
              {it.ok ? <CheckCircle2 style={{ height: 18, width: 18 }} className="text-emerald-500" /> : <Circle style={{ height: 18, width: 18 }} className="text-slate-300" />}
              <span className={it.ok ? "text-slate-900 font-medium" : "text-slate-500"}>{it.label} {it.required && !it.ok && <span className="text-red-500 font-bold">*</span>}</span>
            </div>
            {!it.ok && <button onClick={() => goTo(it.stepKey)} className="text-xs font-bold text-sky-600 hover:underline" data-testid={`close-goto-${it.stepKey}`}>Compléter</button>}
          </div>
        ))}
      </div>
      {!canConfirm && (
        <div className="mt-3 flex items-start gap-2 rounded-xl bg-amber-50 border border-amber-200 px-4 py-3 text-xs font-semibold text-amber-700" data-testid="close-blocked-banner">
          <AlertTriangle className="h-4 w-4 shrink-0" />
          La clôture nécessite le kilométrage, la signature du client (ou un refus documenté){checklist.caution_required ? " et une décision de caution" : ""}.
        </div>
      )}
      <button onClick={confirm} disabled={!canConfirm || confirming}
        className="mt-4 w-full inline-flex items-center justify-center gap-2 py-3.5 rounded-xl bg-emerald-600 text-white text-sm font-bold hover:bg-emerald-700 disabled:opacity-40 disabled:cursor-not-allowed"
        data-testid="close-confirm-btn">
        {confirming ? <Loader2 className="h-4 w-4 animate-spin" /> : <KeyRound className="h-4 w-4" />}
        Confirmer le retour et clôturer la location
      </button>
    </StepCard>
  );
}
